import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Flag, FlagOff } from "lucide-react";
import { API, errMsg, TOPIC_NAMES, fmtDate } from "../lib/api";
import { GuideContent } from "../components/GuideContent";
import { toast } from "sonner";

export default function GuideView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [flagged, setFlagged] = useState(false);

  useEffect(() => {
    setGuide(null);
    API.get(`/guides/${id}`).then(({ data }) => { setGuide(data); setFlagged(data.flagged); })
      .catch((e) => { toast.error(errMsg(e)); navigate("/app/guides"); });
  }, [id, navigate]);

  const toggleFlag = async () => {
    try {
      if (flagged) {
        await API.delete(`/guides/${id}/flag`);
        toast.success("Flag removed");
      } else {
        await API.post(`/guides/${id}/flag`);
        toast.success("Guide flagged — find it under Review → Revision guides");
      }
      setFlagged(!flagged);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!guide) return <p className="py-16 text-center text-sm text-muted-foreground">Loading guide…</p>;

  return (
    <div data-testid="guide-view" className="max-w-3xl">
      <button data-testid="back-to-guides-btn" onClick={() => navigate("/app/guides")}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> All revision guides
      </button>
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">
          {TOPIC_NAMES[guide.topic] || guide.topic}
        </span>
        {guide.published_at && (
          <span className="text-xs text-muted-foreground">· Published {fmtDate(guide.published_at)}</span>
        )}
      </div>
      <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight" data-testid="guide-title">{guide.title}</h1>
      <button data-testid="flag-guide-btn" onClick={toggleFlag}
        className={`mt-4 flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md border transition-colors ${
          flagged ? "border-accent bg-accent/20 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
        {flagged ? <FlagOff className="w-3.5 h-3.5" /> : <Flag className="w-3.5 h-3.5" />}
        {flagged ? "Flagged" : "Flag this guide"}
      </button>
      <div className="mt-6 bg-card border border-border rounded-lg p-6 md:p-10 shadow-sm" data-testid="guide-body">
        <GuideContent body={guide.body} />
      </div>
    </div>
  );
}
